import React, { useCallback, useMemo } from "react";
import { Chessboard } from "react-chessboard";

import type {
    ChessboardOptions,
    PieceHandlerArgs,
    PieceDropHandlerArgs,
    PieceDataType,
} from "react-chessboard";

export interface TudorChessboardProps {
    position: string;
    onPieceDrop?: (
        sourceSquare: string,
        targetSquare: string,
        piece: string
    ) => boolean;
    boardOrientation?: "white" | "black";
    arePiecesDraggable?: boolean;
    /**
     * Multiplayer helper: return true only for pieces that the local player
     * is allowed to move (e.g. only "w*" or only "b*").
     * Receives the classic "wP"/"bK" style piece string.
     */
    isDraggablePiece?: (args: {
        piece: string;
        sourceSquare: string;
    }) => boolean;
}

const TUDOR_THEME = {
    lightSquare: "#c5a059",
    darkSquare: "#2e2c2cff",
    dropSquare: "#eab308",
};


export const TudorChessboard: React.FC<TudorChessboardProps> = ({
    position,
    onPieceDrop,
    boardOrientation = "white",
    arePiecesDraggable = true,
    isDraggablePiece,
}) => {
    const handlePieceDrop = useCallback(
        ({ sourceSquare, targetSquare, piece }: PieceDropHandlerArgs): boolean => {
            if (!targetSquare) return false;

            const pieceId = piece.pieceType;

            return onPieceDrop
                ? onPieceDrop(sourceSquare, targetSquare, pieceId)
                : true;
        },
        [onPieceDrop]
    );

    const canDragPiece = useCallback(
        ({ piece, square }: PieceHandlerArgs): boolean => {
            if (!square) return false;
            if (!arePiecesDraggable) return false;

            const pieceId = piece.pieceType; // "wP", "bK", ...

            if (!pieceId) return false;

            // If caller provided custom multiplayer logic, defer to it
            if (isDraggablePiece) {
                return isDraggablePiece({ piece: pieceId, sourceSquare: square });
            }

            // Default behaviour (good fallback for multiplayer):
            // only drag pieces that match the board orientation colour.
            const color = pieceId[0]; // 'w' or 'b'
            return boardOrientation === "white" ? color === "w" : color === "b";
        },
        [arePiecesDraggable, isDraggablePiece, boardOrientation]
    );

    const options: ChessboardOptions = useMemo(
        () => ({
            position,
            boardOrientation,
            allowDragging: arePiecesDraggable,
            canDragPiece,
            onPieceDrop: handlePieceDrop,
            // v5 style keys:
            darkSquareStyle: { backgroundColor: "transparent" },
            lightSquareStyle: { backgroundColor: "transparent" },
            dropSquareStyle: {
                boxShadow: `inset 0 0 1px 6px ${TUDOR_THEME.dropSquare}`,
            },
            boardStyle: {
                borderRadius: "4px",
                boxShadow: "none",
            },
        }),
        [position, boardOrientation, arePiecesDraggable, canDragPiece, handlePieceDrop]
    );

    return (
        <div
            id="TudorBoard"
            className="w-full h-full rounded-lg overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.8)] border-[8px] border-tudor-gray relative"
        >
            {/* Background grid */}
            <div className="absolute inset-0 grid grid-cols-8 grid-rows-8 z-0 pointer-events-none">
                {Array.from({ length: 64 }).map((_, i) => {
                    const row = Math.floor(i / 8);
                    const col = i % 8;
                    const isDark = (row + col) % 2 === 1;
                    return (
                        <div
                            key={i}
                            className="w-full h-full"
                            style={{
                                backgroundColor: isDark
                                    ? TUDOR_THEME.darkSquare
                                    : TUDOR_THEME.lightSquare,
                            }}
                        />
                    );
                })}
            </div>

            <div className="absolute inset-0 pointer-events-none z-10 shadow-inner" />

            {/* Chessboard: NEVER CHANGE THE WAY PROPS ARE LOADED THERE IS ONLY THIS WAY */}
            {/* DONT EVEN THINK IT, NO YOU DONT NEED TOS SPREAD THE PROPS DONT APPLY THEM DIRECTLY IT DOES NOT WORK EVER*/}
            <div className="relative z-20 w-full h-full">
                <Chessboard options={options} />
            </div>
        </div>
    );
};
